import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ExclamationTriangleIcon, InformationCircleIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import CustomDialog from "../modals/CustomDialog/CustomDialog";
import { Button } from "../forms/Buttons/Button";
import OverflowMenu from "../ui/OverflowMenu";
import { hasForeignImage, isBlankHtml, RichText } from "../KaTeXComponent/RichText";
import { canCreateIn, canEditAllIn, folderPath, isAdminUser } from "../../utils/folders";
import { UserContext } from "../../utils/contexts/UserContext";
import { getApiErrorCode, getApiErrorMessage } from "../../utils/apiError";
import { BookletLinks, linksBooklets, lockingBooklets } from "../../pages/QuestionPage/review/bookletLock";
import { isCanceledError } from "../../utils/examError";
import {
  createPassage,
  deletePassage,
  getPassage,
  patchPassage,
  setPassageQuestions,
  uploadPassageBody,
} from "../../services/passageService";
import { focusRing, linkButtonClass, Spinner, StatusBadge } from "../../pages/Exams/TestBuilder/ui";
import PassageEditor, { emptyPassageForm, PassageNameField } from "./PassageEditor";
import PassageLockNotice, { passageBooklets } from "./PassageLock";
import { listedNames, passageErrorMessage } from "./passageErrors";
import QuestionPicker from "./QuestionPicker";
import { forgetPassage, rememberPassage } from "./usePassage";
import useBankLists from "./useBankLists";

const formOf = (detail) => ({
  name: detail?.name || "",
  subjectUuid: detail?.subject?.uuid || null,
  folderUuid: detail?.folder?.uuid || null,
  body: detail?.body || "",
  file: null,
});
const sameIds = (a, b) => a.length === b.length && a.every((id, i) => id === b[i]);
// Write responses are only trusted as the full detail when they carry the text and the questions.
export const isPassageDetail = (data) => !!data && typeof data.body === "string" && Array.isArray(data.questions);

// The passage dialogs' secondary actions (Close, Cancel, Back) next to their one main button: quiet, as tall as the
// Button beside them (40px, 44px on phones). A Close left alone (nothing to save) keeps a frame, as a button.
export const quietDialogButton = `inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-4 text-sm font-medium text-slate-600 transition duration-200 enabled:hover:bg-slate-100 enabled:hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 md:h-10 ${focusRing}`;
const framedDialogButton = `inline-flex h-11 items-center justify-center whitespace-nowrap rounded-lg bg-white px-5 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 enabled:hover:bg-slate-50 enabled:hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 md:h-10 ${focusRing}`;

// The form's problems before anything is sent; {} when it can be saved. `requireFolder`: without the global
// rights a passage goes into a folder's subject (the server answers folder_required).
export const passageFormErrors = (t, form, { requireFolder = false } = {}) => {
  const errors = {};
  if (!form.name.trim()) errors.name = t("passages.editor.name_required");
  if (requireFolder && !form.folderUuid) errors.folder = t("passages.errors.folder_required");
  if (form.folderUuid && !form.subjectUuid) errors.subject = t("passages.editor.folder_needs_subject");
  if (!form.file) {
    if (isBlankHtml(form.body)) errors.body = t("passages.editor.body_required");
    else if (hasForeignImage(form.body)) errors.body = t("passages.editor.foreign_images");
  }
  return errors;
};

/**
 * A passage of the bank: its text and its questions, in order. `passageUuid` null creates a new one.
 * Writers edit, link and unlink questions (unlinking questions that stand in tests asks first); the passage's
 * "⋯" (top right) deletes it, once no question is linked, for those the server lets delete it (an admin, the
 * subject's moderator, an author their own; the global rights outside folders). Others only read. In a folder's
 * subject the role decides (moderator, expert and admin write any passage, an author their own); a passage
 * without a folder, the global rights (create_question or create_exam). A passage's questions live in its folder.
 * `defaultFolder` (a subject folder) is where a new passage starts.
 * A printed passage (its detail's `booklets`: active BTS booklets print its questions) opens read-only under a lock
 * notice, admins getting the booklets' links: neither its text, its folder nor its questions change until the
 * booklets are back in draft. Only its name still does, for those whose role edits every passage of the subject (an
 * author's own passage is theirs only until a reviewer takes up one of its questions, and a printed one is approved).
 */
export default function PassageDialog({ isOpen, passageUuid, onClose, onSaved, onDeleted, defaultFolder = null }) {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const perms = user?.perms || [];
  const { folders, fresh: listsLoaded } = useBankLists(isOpen);
  const isAdmin = isAdminUser(user);
  const hasGlobalWrite = perms.includes("create_question") || perms.includes("create_exam");

  const [uuid, setUuid] = useState(passageUuid || null); // set once a new passage is created
  const [detail, setDetail] = useState(null);
  const [loadState, setLoadState] = useState({ loading: false, error: null });
  const [form, setForm] = useState(emptyPassageForm);
  const [initialForm, setInitialForm] = useState(emptyPassageForm);
  const [bodyEdited, setBodyEdited] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const [members, setMembers] = useState([]);
  const [savedIds, setSavedIds] = useState([]);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  // For an admin, the active BTS booklets that froze the passage's questions (question_in_booklet): their links.
  const [errorBooklets, setErrorBooklets] = useState([]);
  const [confirmTests, setConfirmTests] = useState(null); // tests listed by question_in_tests
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false); // a picture is being uploaded into the text
  const [attempt, setAttempt] = useState(0);
  // Read when the dialog opens, so a later change does not reset a form being filled.
  const defaultFolderRef = useRef(defaultFolder);
  defaultFolderRef.current = defaultFolder;

  useEffect(() => {
    if (!isOpen) return undefined;
    setUuid(passageUuid || null);
    setErrors({});
    setError("");
    setConfirmTests(null);
    setBodyEdited(false);
    if (!passageUuid) {
      const start = defaultFolderRef.current;
      const fresh = start
        ? { ...emptyPassageForm, folderUuid: start.uuid, subjectUuid: start.subject?.uuid || null }
        : emptyPassageForm;
      setDetail(null);
      setForm(fresh);
      setInitialForm(fresh);
      setMembers([]);
      setSavedIds([]);
      setEditorKey((k) => k + 1);
      setLoadState({ loading: false, error: null });
      return undefined;
    }
    const controller = new AbortController();
    // Nothing of a previously opened passage may count as unsaved changes of this one.
    setForm(emptyPassageForm);
    setInitialForm(emptyPassageForm);
    setMembers([]);
    setSavedIds([]);
    setLoadState({ loading: true, error: null });
    getPassage(passageUuid, { signal: controller.signal })
      .then((data) => {
        const next = formOf(data);
        setDetail(data);
        setForm(next);
        setInitialForm(next);
        setMembers(data.questions || []);
        setSavedIds((data.questions || []).map((q) => q.uuid));
        setEditorKey((k) => k + 1);
        setLoadState({ loading: false, error: null });
      })
      .catch((err) => {
        if (!isCanceledError(err)) setLoadState({ loading: false, error: err });
      });
    return () => controller.abort();
  }, [isOpen, passageUuid, attempt]);

  const memberIds = useMemo(() => members.map((q) => q.uuid), [members]);
  // The rights come from the saved passage's folder: the user's role there, or the global rights outside.
  const savedFolder = detail?.folder?.uuid || null;
  const folderRole = savedFolder ? folders.find((f) => f.uuid === savedFolder)?.role || null : null;
  const ownPassage = !!detail?.author?.uuid && detail.author.uuid === user?.uuid;
  const canWrite = !uuid
    ? hasGlobalWrite || isAdmin || folders.some((f) => canCreateIn(f.role))
    : savedFolder
      ? canEditAllIn(folderRole) || (folderRole === "author" && ownPassage)
      : hasGlobalWrite;
  // Who deletes a passage (once no question is linked): an admin, the subject's moderator, an author their own;
  // outside folders the global rights. An expert edits passages but deletes none.
  const canDelete =
    isAdmin || (savedFolder ? folderRole === "admin" || folderRole === "moderator" || (folderRole === "author" && ownPassage) : hasGlobalWrite);
  // Questions for the passage are written where its folder lets the role create (or with create_question
  // outside folders); they are created in the passage's folder.
  const canCreateQuestion = savedFolder ? canCreateIn(folderRole) : perms.includes("create_question");
  const requireFolder = !hasGlobalWrite && !isAdmin;
  // The passage loads; a new one waits for the folder lists when it has to go into a folder's subject.
  const waiting = loadState.loading || (!uuid && requireFolder && !listsLoaded);
  const ready = !loadState.loading && !loadState.error;
  // Printed: active BTS booklets print the passage's questions (see above).
  const booklets = uuid ? passageBooklets(detail) : [];
  const locked = booklets.length > 0;
  const canRename = canWrite && !(savedFolder && folderRole === "author");
  const canSave = ready && canWrite && (!locked || canRename);
  const fieldsChanged =
    form.name !== initialForm.name || form.subjectUuid !== initialForm.subjectUuid || form.folderUuid !== initialForm.folderUuid;
  const dirty = fieldsChanged || bodyEdited || !!form.file || !sameIds(memberIds, savedIds);
  const busy = saving || deleting;
  // Linked on the server but taken out of the selection: offered again in the picker.
  const unlinked = useMemo(() => (detail?.questions || []).filter((q) => !memberIds.includes(q.uuid)), [detail, memberIds]);
  const tests = detail?.usage?.tests || [];

  const update = (patch) => {
    const { bodyEdited: edited, ...rest } = patch;
    if (edited) setBodyEdited(true);
    setForm((prev) => ({ ...prev, ...rest }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(rest).forEach((key) => {
        if (key === "name") delete next.name;
        if (key === "subjectUuid" || key === "folderUuid") delete next.subject;
        if (key === "folderUuid") delete next.folder;
        if (key === "body" || key === "file") delete next.body;
      });
      return next;
    });
  };

  // A refused save or delete: its text, and the booklets' links for an admin. `linking`: set_questions was refused.
  const showError = (err, fallbackKey, known, { linking = false } = {}) => {
    const refusing = getApiErrorCode(err) === "question_in_booklet" ? lockingBooklets(err) : [];
    const linked = linksBooklets(user, refusing);
    setError(passageErrorMessage(t, err, fallbackKey, known, { linked, linking }));
    setErrorBooklets(linked ? refusing : []);
  };

  const close = () => {
    if (busy) return;
    if (canWrite && ready && dirty && !window.confirm(t("passages.dialog.discard_confirm"))) return;
    onClose();
  };

  const save = async ({ confirm = false } = {}) => {
    if (uploading) return;
    const problems = passageFormErrors(t, form, { requireFolder });
    // A printed passage changes its name only: the rest of the form is what the server has.
    const shown = locked ? (problems.name ? { name: problems.name } : {}) : problems;
    setErrors(shown);
    if (Object.keys(shown).length) return;
    setSaving(true);
    setError("");
    setConfirmTests(null);
    const isNew = !uuid;
    const fields = { name: form.name.trim(), subject_uuid: form.subjectUuid || null, folder_uuid: form.folderUuid || null };
    let current = uuid;
    let saved = null;
    let wrote = false; // part of the change reached the server
    let linking = false; // the questions are being linked (set_questions)
    try {
      if (!current) {
        saved = await createPassage({ ...fields, body: form.file ? "" : form.body });
        current = saved.uuid;
        wrote = true;
        setUuid(current);
        setInitialForm({ ...form, name: fields.name, file: null });
        setBodyEdited(false);
      } else if (fieldsChanged || (bodyEdited && !form.file)) {
        saved = await patchPassage(current, { ...fields, ...(bodyEdited && !form.file ? { body: form.body } : {}) });
        wrote = true;
        setInitialForm({ ...form, name: fields.name, file: null });
        setBodyEdited(false);
      }
      if (form.file) {
        saved = await uploadPassageBody(current, form.file);
        wrote = true;
        if (!isPassageDetail(saved)) saved = await getPassage(current);
        setForm((prev) => ({ ...prev, file: null, body: saved.body || "" }));
        setEditorKey((k) => k + 1);
      }
      if (!sameIds(memberIds, savedIds)) {
        linking = true;
        saved = await setPassageQuestions(current, memberIds, { confirm });
        linking = false;
        wrote = true;
        setSavedIds(memberIds);
      }
      const final = isPassageDetail(saved) ? saved : await getPassage(current);
      rememberPassage(final);
      toast.success(isNew ? t("passages.dialog.created") : t("passages.dialog.saved"));
      onSaved?.(final);
      onClose();
    } catch (err) {
      // What was already saved must show up in the list and in open panels even though the rest failed.
      if (wrote) {
        getPassage(current).then(rememberPassage).catch(() => {});
        onSaved?.({ uuid: current, name: fields.name });
      }
      // set_questions refuses a printed question (question_in_booklet) before it asks about the tests, and
      // `confirm` does not get past that: the confirmation is only offered for question_in_tests.
      if (getApiErrorCode(err) === "question_in_tests") {
        setConfirmTests(err?.response?.data?.tests || []);
      } else {
        // A refused move or link names its questions (those of the passage and the ones picked).
        showError(err, "passages.dialog.save_failed", [...(detail?.questions || []), ...members], { linking });
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!uuid || !window.confirm(t("passages.dialog.delete_confirm", { name: form.name || detail?.name }))) return;
    setDeleting(true);
    setError("");
    try {
      await deletePassage(uuid);
      forgetPassage(uuid);
      toast.success(t("passages.dialog.deleted"));
      onDeleted?.(uuid);
      onClose();
    } catch (err) {
      showError(err, "passages.dialog.delete_failed");
    } finally {
      setDeleting(false);
    }
  };

  const createQuestion = () => {
    if (dirty && !window.confirm(t("passages.dialog.discard_confirm"))) return;
    onClose();
    // In the passage's folder: the subject is fixed and the questions (and their drafts) go there.
    navigate(`/my-questions/create?passage=${uuid}${savedFolder ? `&folder=${savedFolder}` : ""}`);
  };

  // The rare and destructive actions, in "⋯": deleting waits until no question is linked (the item says so).
  const blocked = savedIds.length > 0;
  const menuItems =
    ready && canWrite && uuid && !locked && canDelete
      ? [
          {
            key: "delete",
            label: blocked ? (
              <>
                <span className="block">{t("passages.dialog.delete")}</span>
                <span className="mt-0.5 block text-xs font-normal text-slate-500">{t("passages.dialog.delete_disabled")}</span>
              </>
            ) : (
              t("passages.dialog.delete")
            ),
            Icon: TrashIcon,
            danger: true,
            disabled: busy || blocked,
            onClick: remove,
          },
        ]
      : [];

  const errorBox = error && (
    <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
      {error}
      <BookletLinks booklets={errorBooklets} />
    </p>
  );

  const title = uuid ? t("passages.dialog.title") : t("passages.dialog.title_new");

  return (
    <CustomDialog isOpen={isOpen} closeModal={close} headerText={title} maxWidth="max-w-6xl">
      {/* relative: the "⋯" sits on the title's line, at the dialog's top right (this box starts 16px under the
          title: the content's mt-4 collapses into it). */}
      <div className="relative">
        {menuItems.length > 0 && (
          <div className="absolute -right-3 -top-[3.125rem] md:-right-2 md:-top-[2.875rem]">
            <OverflowMenu items={menuItems} label={t("passages.dialog.actions")} testId="passage-actions" />
          </div>
        )}
        {waiting ? (
          <div className="flex justify-center py-12">
            <Spinner className="h-6 w-6" />
          </div>
        ) : loadState.error ? (
          <div role="alert" className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            {getApiErrorMessage(t, loadState.error, "passages.dialog.load_failed")}
            <button type="button" onClick={() => setAttempt((n) => n + 1)} className={linkButtonClass}>
              {t("passages.panel.retry")}
            </button>
          </div>
        ) : !canWrite ? (
          <ReadOnlyPassage detail={detail} />
        ) : locked ? (
          <ReadOnlyPassage
            detail={detail}
            notice={<PassageLockNotice booklets={booklets} extra={canRename ? t("passages.lock.name_only") : ""} />}
            name={
              canRename ? (
                <PassageNameField value={form.name} onChange={(name) => update({ name })} error={errors.name} disabled={busy} />
              ) : null
            }
          >
            {errorBox}
          </ReadOnlyPassage>
        ) : (
          <div className="mt-4 space-y-6">
            {tests.length > 0 && (
              <div className="flex gap-2 rounded-xl border border-blue-100 bg-blue-50/70 px-3 py-2 text-sm text-blue-900">
                <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />
                <p>
                  {t("passages.dialog.usage", { tests: listedNames(tests) })}{" "}
                  <span className="text-blue-800">{t("passages.dialog.usage_warning")}</span>
                </p>
              </div>
            )}

            <PassageEditor
              value={form}
              onChange={update}
              editorKey={editorKey}
              errors={errors}
              disabled={busy}
              onUploadingChange={setUploading}
              keepFolderUuid={initialForm.folderUuid}
              allowNoFolder={!requireFolder}
            />
            {uuid && savedIds.length > 0 && form.folderUuid !== initialForm.folderUuid && (
              <p className="-mt-3 flex gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
                {t("passages.dialog.move_with_questions", { count: savedIds.length })}
              </p>
            )}

            <section className="space-y-3 border-t border-slate-100 pt-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h4 className="text-base font-semibold text-slate-900">{t("passages.dialog.questions")}</h4>
                {uuid && canCreateQuestion && (
                  <button type="button" onClick={createQuestion} disabled={busy} className={`${linkButtonClass} max-md:min-h-[2.75rem]`}>
                    <PlusIcon className="h-4 w-4" aria-hidden="true" />
                    {t("passages.dialog.create_question")}
                  </button>
                )}
              </div>
              <QuestionPicker
                selected={members}
                onChange={(next) => {
                  setMembers(next);
                  setConfirmTests(null);
                }}
                passageUuid={uuid}
                subject={form.subjectUuid}
                folder={form.folderUuid || "null"}
                extraRows={unlinked}
              />
            </section>

            {confirmTests && (
              <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <div className="flex gap-2">
                  <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" aria-hidden="true" />
                  <div className="space-y-1">
                    <p className="font-semibold">{t("passages.dialog.unlink_confirm_title")}</p>
                    <p>{t("passages.dialog.unlink_confirm_text", { tests: listedNames(confirmTests) })}</p>
                  </div>
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <Button type="button" color="secondary-outline" text={t("buttons.cancel")} onClick={() => setConfirmTests(null)} />
                  <Button
                    type="button"
                    color="warning"
                    text={t("passages.dialog.unlink_confirm_button")}
                    loading={saving}
                    onClick={() => save({ confirm: true })}
                  />
                </div>
              </div>
            )}

            {errorBox}
          </div>
        )}
      </div>

      {/* One main button; "Close" stays quiet beside it, framed once it is alone (nothing to save). One Close all
          along, only its look changes: the focus the dialog gives it on opening stays there once the passage has
          loaded. Phones: Save fills the row. */}
      <div className="mt-6 flex items-center justify-end gap-2">
        <button type="button" onClick={close} disabled={busy} className={waiting || canSave ? quietDialogButton : framedDialogButton}>
          {t("buttons.close")}
        </button>
        {canSave && (
          <Button
            type="button"
            color="primary"
            text={t("buttons.save")}
            onClick={() => save()}
            loading={saving}
            disabled={deleting || uploading}
            className="max-md:h-11 max-md:flex-1"
          />
        )}
      </div>
    </CustomDialog>
  );
}

/**
 * The passage as it is: its name and place, its text and its questions with their statuses. `notice` replaces the
 * "View only" caption (the lock of a printed passage); `name` replaces the name's heading (the field of a printed
 * passage); `children` go last (e.g. a refusal).
 */
function ReadOnlyPassage({ detail, notice = null, name = null, children = null }) {
  const [t] = useTranslation();
  if (!detail) return null;
  const place = folderPath(detail.folder) || detail.subject?.name || "";
  return (
    <div className="mt-4 space-y-5">
      {notice || <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{t("passages.dialog.read_only")}</p>}
      {name ? (
        <div className="space-y-2">
          {name}
          {place && <p className="text-sm text-slate-500">{place}</p>}
        </div>
      ) : (
        <div>
          <h4 className="text-base font-semibold text-slate-900">{detail.name}</h4>
          <p className="text-sm text-slate-500">{place}</p>
        </div>
      )}
      <div className="passage-content max-h-[28rem] overflow-y-auto rounded-xl border border-slate-200 p-4 text-sm leading-relaxed text-slate-800 [&_img]:max-h-80 [&_img]:!w-auto">
        <RichText html={detail.body} dollarMath={false} />
      </div>
      <section className="space-y-2">
        <h4 className="text-sm font-semibold text-slate-900">{t("passages.dialog.questions")}</h4>
        {detail.questions?.length ? (
          <ol className="space-y-1.5">
            {detail.questions.map((q, i) => (
              <li key={q.uuid} className="flex items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5">
                <span className="w-6 shrink-0 text-right text-sm font-semibold tabular-nums text-indigo-700">{i + 1}</span>
                <span className="min-w-0 flex-1 truncate text-sm text-slate-900">{q.name || q.code}</span>
                <StatusBadge status={q.status} />
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-slate-500">{t("passages.dialog.no_questions")}</p>
        )}
      </section>
      {children}
    </div>
  );
}
